import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { listEvents } from '@/lib/events-api';
import {
  createUploadSession,
  sendSubmissionBytes,
  completeSubmissionSession,
} from '@/lib/submissions-api';
import {
  ArrowLeft, Camera, Image as ImageIcon, Lock, Upload, X,
  RefreshCw, CheckCircle, AlertCircle, Film, Check, Square, CheckSquare,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';

function humanBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function ContribUploadPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ready } = useRequireAuth();
  const [sp] = useSearchParams();
  const wantedEventId = sp.get('event_id') || '';
  const initialMode = sp.get('mode') || '';

  const [attending, setAttending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [sending, setSending] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [openedInitial, setOpenedInitial] = useState(false);
  const [uploadMode, setUploadMode] = useState(null); // null | 'send-all' | 'review'
  const [pendingFiles, setPendingFiles] = useState([]);
  const [quota, setQuota] = useState(null);
  const [quotaWarning, setQuotaWarning] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await listEvents();
      setAttending(body.attending || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  const active = useMemo(() => {
    if (attending.length === 0) return null;
    if (wantedEventId) {
      const match = attending.find(a => a.event.id === wantedEventId);
      if (match) return match;
    }
    return attending[0];
  }, [attending, wantedEventId]);

  const canUpload = !!active?.membership?.canUpload;
  const rules = active?.event?.media_rules;

  // Auto-open file picker based on mode param
  useEffect(() => {
    if (!ready || openedInitial || loading || !active || !canUpload || !initialMode) return;
    setOpenedInitial(true);
    if (initialMode === 'camera' && cameraInputRef.current) {
      cameraInputRef.current.click();
    } else if (initialMode === 'library' && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [ready, openedInitial, loading, active, canUpload, initialMode]);

  async function onFilesSelected(e) {
    const sel = Array.from(e.target.files || []);
    e.target.value = '';
    if (sel.length === 0) return;
    setGlobalError(null);
    setQuotaWarning(null);

    // Check quota
    let remaining = Infinity;
    try {
      if (active?.event?.id) {
        const q = await apiFetch(`/api/v1/events/${encodeURIComponent(active.event.id)}/upload-quota`);
        setQuota(q);
        remaining = q.remaining ?? Infinity;
      }
    } catch { /* ignore - proceed without quota guard */ }

    if (sel.length > remaining && remaining !== Infinity) {
      setQuotaWarning(`${sel.length} picked, only ${remaining} fit within this event\u2019s limit.`);
    }

    // Build items with preview URLs
    const built = sel.map((f, i) => ({
      key: `${Date.now()}-${i}-${Math.random().toString(16).slice(2, 8)}`,
      file: f, filename: f.name, mime: f.type || 'image/jpeg', size: f.size,
      isVideo: f.type?.startsWith('video/') || false, caption: '', moment: '',
      progress: 0, status: 'READY', preview: URL.createObjectURL(f),
      errorMessage: null, serverSubmissionId: null,
    }));

    if (built.length === 1) {
      // Single file → skip chooser, go straight to queue
      setItems(prev => [...prev, ...built]);
      setUploadMode(null);
      setPendingFiles([]);
      setSelected(new Set());
    } else {
      // Multiple files → show "Send all N" vs "Review & choose"
      setPendingFiles(built);
      setSelected(new Set(built.map(b => b.key)));
      setUploadMode('chooser');
    }
  }

  function updateItem(key, patch) {
    setItems(prev => prev.map(it => it.key === key ? { ...it, ...patch } : it));
  }
  function removeItem(key) {
    setItems(prev => prev.filter(it => it.key !== key));
  }

  const totalSize = useMemo(
    () => items.filter(i => i.status !== 'CANCELLED').reduce((a, b) => a + (b.size || 0), 0),
    [items],
  );
  const readyCount = items.filter(i => i.status === 'READY' || i.status === 'FAILED').length;
  const deliveredCount = items.filter(i => i.status === 'DELIVERED').length;
  const anyDelivered = deliveredCount > 0;

  async function sendOne(it, eventId) {
    updateItem(it.key, { status: 'UPLOADING', progress: 0, errorMessage: null });
    try {
      const session = await createUploadSession(eventId, {
        filename: it.filename,
        mime_type: it.mime,
        size_bytes: it.size,
        caption: it.caption || undefined,
        event_moment_tag: it.moment || undefined,
      });
      await sendSubmissionBytes(eventId, session.id, it.file, it.filename, (loaded, total) => {
        updateItem(it.key, { progress: total ? loaded / total : 0 });
      });
      const done = await completeSubmissionSession(eventId, session.id);
      updateItem(it.key, {
        status: 'DELIVERED',
        progress: 1,
        serverSubmissionId: done.submission?.id,
      });
    } catch (e) {
      updateItem(it.key, {
        status: 'FAILED',
        errorMessage: e.message || 'Upload failed.',
      });
    }
  }

  async function sendToHost() {
    if (!active) return;
    if (readyCount === 0) return;
    setSending(true);
    setGlobalError(null);
    try {
      const eventId = active.event.id;
      const pending = items.filter(i => i.status === 'READY' || i.status === 'FAILED');
      for (const it of pending) {
        const fresh = items.find(x => x.key === it.key) || it;
        await sendOne(fresh, eventId);
      }
    } finally {
      setSending(false);
    }
  }

  async function retryItem(key) {
    if (!active) return;
    const it = items.find(x => x.key === key);
    if (!it) return;
    await sendOne(it, active.event.id);
  }

  const acceptTypes = rules?.allow_videos ? 'image/*,video/*' : 'image/*';

  function confirmSendAll() {
    setItems(prev => [...prev, ...pendingFiles]);
    setPendingFiles([]);
    setUploadMode(null);
    setSelected(new Set());
  }

  function confirmReviewSelection() {
    const chosen = pendingFiles.filter(f => selected.has(f.key));
    if (chosen.length === 0) return;
    setItems(prev => [...prev, ...chosen]);
    setPendingFiles([]);
    setUploadMode(null);
    setSelected(new Set());
  }

  function toggleSelect(key) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function selectAll() { setSelected(new Set(pendingFiles.map(f => f.key))); }
  function deselectAll() { setSelected(new Set()); }

  return (
    <div className="max-w-lg mx-auto px-4 py-6" data-testid="contrib-upload-screen">
      <button onClick={() => navigate('/contrib-event', { replace: true })} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="contrib-upload-back">
        <ArrowLeft className="w-4 h-4" /> back to event
      </button>

      <h1 className="text-2xl font-extrabold text-[#0B1B46] mb-1" data-testid="contrib-upload-title">
        Contribute to {active?.event?.name || 'your event'}
      </h1>
      <p className="text-sm text-[#5B6472] mb-4">
        Select photos or videos to send to the host. Nothing is shared with other attendees.
      </p>

      {loading ? (
        <div className="flex justify-center py-8" data-testid="contrib-upload-loading">
          <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !active ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid="contrib-upload-no-event">
          <p className="font-bold text-[#0B1B46]">Join an event to start contributing</p>
          <p className="text-sm text-[#5B6472] mt-1">You need to be a member of an active event before you can upload.</p>
          <button onClick={() => navigate('/join')} className="w-full h-10 mt-3 bg-[#146BFF] text-white font-semibold rounded-lg hover:bg-[#0F55CC] transition-colors" data-testid="contrib-upload-join-btn">Join an Event</button>
        </div>
      ) : !canUpload ? (
        <div className="bg-[#FBEEDA] border border-[#B86500]/20 rounded-xl p-4" data-testid="contrib-upload-locked">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-[#B86500] mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-[#0B1B46]">Uploads locked</p>
              <p className="text-sm text-[#5B6472] mt-1">
                You need verified attendance or host approval before uploads unlock. Your current status:{' '}
                <span className="font-bold text-[#0B1B46]" data-testid="contrib-upload-status">{active.membership.status}</span>.
              </p>
              {active.membership.status === 'HOST_PENDING' ? (
                <button onClick={() => navigate(`/host-approval-required?event_id=${encodeURIComponent(active.event.id)}`)}
                  className="w-full h-10 mt-3 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
                  data-testid="contrib-upload-approval-btn">View approval status</button>
              ) : (
                <button onClick={() => navigate(`/verify-attendance?event_id=${encodeURIComponent(active.event.id)}`)}
                  className="w-full h-10 mt-3 bg-[#146BFF] text-white font-semibold rounded-lg hover:bg-[#0F55CC] transition-colors"
                  data-testid="contrib-upload-verify-btn">Verify My Attendance</button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* File pickers */}
          <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="contrib-upload-picker">
            <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">Add media</p>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={onFilesSelected} className="hidden" data-testid="contrib-upload-camera-input" />
            <input ref={fileInputRef} type="file" accept={acceptTypes} multiple onChange={onFilesSelected} className="hidden" data-testid="contrib-upload-library-input" />
            <div className="flex gap-3">
              <button onClick={() => cameraInputRef.current?.click()}
                className="flex-1 h-11 flex items-center justify-center gap-2 bg-white border border-[#E3E8EF] rounded-lg font-semibold text-sm text-[#0B1B46] hover:bg-[#F3F5F8] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
                data-testid="contrib-upload-camera-btn">
                <Camera className="w-4 h-4" /> Take Photos
              </button>
              <button onClick={() => fileInputRef.current?.click()}
                className="flex-1 h-11 flex items-center justify-center gap-2 bg-[#146BFF] hover:bg-[#0F55CC] text-white rounded-lg font-semibold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2"
                data-testid="contrib-upload-library-btn">
                <ImageIcon className="w-4 h-4" /> Choose from Phone
              </button>
            </div>
            {rules && (
              <p className="text-xs text-[#5B6472] mt-2" data-testid="contrib-upload-rules">
                {rules.allow_videos
                  ? `Photos + videos accepted · max ${humanBytes(rules.max_file_bytes)} · max ${rules.max_video_seconds}s video`
                  : `Photos only · max ${humanBytes(rules.max_file_bytes)}`}
              </p>
            )}
            <p className="text-xs text-[#5B6472] mt-1" data-testid="contrib-upload-web-hint">
              Take Photos requires camera access. If your browser blocks it, Choose from Phone works everywhere.
            </p>
          </div>

          {/* Quota warning */}
          {quotaWarning && (
            <div className="bg-[#FFF6E5] border border-[#B86500] rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="contrib-upload-quota-warning">
              <AlertCircle className="w-4 h-4 text-[#B86500] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-[#B86500]">{quotaWarning}</p>
                <p className="text-xs text-[#5B6472] mt-0.5">Remove items or ask the host to upgrade the event.</p>
              </div>
            </div>
          )}

          {/* ─── Upload mode chooser ─── */}
          {uploadMode === 'chooser' && pendingFiles.length > 0 && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="contrib-upload-mode-chooser">
              <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">{pendingFiles.length} files selected</p>
              <div className="flex gap-3 mb-3">
                <button onClick={confirmSendAll} className="flex-1 h-11 bg-[#146BFF] hover:bg-[#0F55CC] text-white rounded-lg font-semibold text-sm transition-colors" data-testid="contrib-upload-send-all-btn">
                  Send all {pendingFiles.length}
                </button>
                <button onClick={() => setUploadMode('review')} className="flex-1 h-11 bg-white border border-[#E3E8EF] rounded-lg font-semibold text-sm text-[#0B1B46] hover:bg-[#F3F5F8] transition-colors" data-testid="contrib-upload-review-btn">
                  Review &amp; choose
                </button>
              </div>
              <button onClick={() => { setPendingFiles([]); setUploadMode(null); setQuotaWarning(null); }} className="text-sm text-[#5B6472] hover:text-[#0B1B46] font-semibold" data-testid="contrib-upload-cancel-selection">Cancel</button>
            </div>
          )}

          {/* ─── Review & Choose grid ─── */}
          {uploadMode === 'review' && pendingFiles.length > 0 && (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="contrib-upload-review-grid">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase">{selected.size} / {pendingFiles.length} selected</p>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs font-bold text-[#146BFF] hover:underline" data-testid="contrib-upload-select-all">Select all</button>
                  <button onClick={deselectAll} className="text-xs font-bold text-[#5B6472] hover:underline" data-testid="contrib-upload-deselect-all">Deselect all</button>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 max-h-80 overflow-y-auto">
                {pendingFiles.map(f => {
                  const isSel = selected.has(f.key);
                  return (
                    <button key={f.key} onClick={() => toggleSelect(f.key)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${isSel ? 'border-[#146BFF]' : 'border-transparent opacity-60'}`}
                      data-testid={`contrib-upload-review-item-${f.key}`}>
                      {f.isVideo ? (
                        <div className="w-full h-full bg-[#0B1B46] flex items-center justify-center"><Film className="w-5 h-5 text-white" /></div>
                      ) : (
                        <img src={f.preview} alt="" className="w-full h-full object-cover" />
                      )}
                      <div className="absolute top-1 right-1">
                        {isSel ? <CheckSquare className="w-5 h-5 text-[#146BFF] bg-white rounded" /> : <Square className="w-5 h-5 text-white bg-black/30 rounded" />}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-3 mt-3">
                <button onClick={confirmReviewSelection} disabled={selected.size === 0}
                  className="flex-1 h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white rounded-lg font-semibold text-sm transition-colors"
                  data-testid="contrib-upload-confirm-selection-btn">
                  Add {selected.size} to queue
                </button>
                <button onClick={() => { setUploadMode('chooser'); }} className="h-11 px-4 bg-white border border-[#E3E8EF] rounded-lg font-semibold text-sm text-[#5B6472] hover:bg-[#F3F5F8] transition-colors" data-testid="contrib-upload-back-to-chooser">Back</button>
              </div>
            </div>
          )}

          {items.length > 0 ? (
            <>
              {/* Summary badges */}
              <div className="bg-white border border-[#E3E8EF] rounded-xl p-3 mb-4 flex flex-wrap gap-2" data-testid="contrib-upload-summary">
                <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">
                  {items.filter(i => i.status !== 'CANCELLED').length} selected
                </span>
                <span className="px-2 py-0.5 text-xs font-bold bg-[#EFF5FF] text-[#146BFF] rounded-full">
                  {humanBytes(totalSize)}
                </span>
                {deliveredCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-bold bg-[#E4F4EC] text-[#1F8A55] rounded-full">
                    ✓ {deliveredCount} delivered
                  </span>
                )}
              </div>

              {/* Queue */}
              <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 mb-4" data-testid="contrib-upload-queue">
                <p className="text-[10px] font-extrabold text-[#0B1B46] tracking-widest uppercase mb-3">Your queue</p>
                <div className="flex flex-col gap-3">
                  {items.map(it => (
                    <QueueRow key={it.key} item={it}
                      onRemove={() => removeItem(it.key)}
                      onRetry={() => retryItem(it.key)}
                      onCaption={t => updateItem(it.key, { caption: t })}
                      onMoment={t => updateItem(it.key, { moment: t })}
                      busy={sending} />
                  ))}
                </div>
              </div>

              {/* Privacy confirmation */}
              <div className="bg-[#EFF5FF] border border-[#D6E4FF] rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="contrib-upload-confirm">
                <Lock className="w-4 h-4 text-[#146BFF] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-[#5B6472]">
                  These files will be privately delivered to the Host. Nothing you upload here becomes visible to other attendees.
                </p>
              </div>

              {/* Send button */}
              <button onClick={sendToHost} disabled={sending || readyCount === 0}
                className="w-full h-11 bg-[#146BFF] hover:bg-[#0F55CC] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] focus:ring-offset-2 mb-3"
                data-testid="contrib-upload-send-btn">
                {sending ? 'Sending…' : `Send to Host (${readyCount})`}
              </button>

              {anyDelivered && (
                <button onClick={() => navigate(`/my-views?event_id=${active.event.id}`, { replace: true })}
                  className="w-full h-11 bg-white border border-[#E3E8EF] text-[#0B1B46] font-semibold rounded-lg hover:bg-[#F3F5F8] transition-colors"
                  data-testid="contrib-upload-my-views-btn">Go to My Views</button>
              )}
            </>
          ) : (
            <div className="bg-white border border-[#E3E8EF] rounded-xl p-4 text-center" data-testid="contrib-upload-empty">
              <Upload className="w-8 h-8 text-[#5B6472] mx-auto mb-2" />
              <p className="text-sm text-[#5B6472]">Nothing queued yet. Tap Take Photos or Choose from Phone to add media.</p>
            </div>
          )}

          {globalError && (
            <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434] mt-3" data-testid="contrib-upload-error">{globalError}</div>
          )}
        </>
      )}
    </div>
  );
}

function QueueRow({ item, onRemove, onRetry, onCaption, onMoment, busy }) {
  const pct = Math.round(item.progress * 100);
  return (
    <div className="flex gap-3 py-3 border-b border-[#E3E8EF] last:border-b-0" data-testid={`contrib-upload-item-${item.key}`}>
      {/* Thumbnail */}
      <div className="w-[72px] h-[72px] rounded-lg overflow-hidden bg-[#F3F5F8] flex-shrink-0">
        {item.isVideo ? (
          <div className="w-full h-full bg-[#0B1B46] flex items-center justify-center">
            <Film className="w-5 h-5 text-white" />
          </div>
        ) : item.preview ? (
          <img src={item.preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-[#5B6472]" />
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#0B1B46] truncate">{item.filename}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
            item.status === 'DELIVERED' ? 'bg-[#E4F4EC] text-[#1F8A55]'
            : item.status === 'FAILED' ? 'bg-[#FBE5E5] text-[#C93434]'
            : item.status === 'UPLOADING' ? 'bg-[#EFF5FF] text-[#146BFF]'
            : 'bg-[#FBEEDA] text-[#B86500]'
          }`} data-testid={`contrib-upload-item-status-${item.key}`}>
            {item.status}
          </span>
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF]">{humanBytes(item.size)}</span>
          {item.isVideo && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#EFF5FF] text-[#146BFF]">▶ video</span>}
        </div>

        {/* Caption + moment inputs (only for not-yet-delivered items) */}
        {item.status !== 'DELIVERED' && (
          <div className="flex flex-col gap-1.5 mt-2">
            <input value={item.caption} onChange={e => onCaption(e.target.value)} placeholder="Caption (optional)"
              className="w-full h-8 px-3 text-xs border border-[#E3E8EF] rounded-md focus:outline-none focus:ring-1 focus:ring-[#146BFF] bg-white"
              data-testid={`contrib-upload-caption-${item.key}`} />
            <input value={item.moment} onChange={e => onMoment(e.target.value)} placeholder="Event moment (e.g. first dance)"
              className="w-full h-8 px-3 text-xs border border-[#E3E8EF] rounded-md focus:outline-none focus:ring-1 focus:ring-[#146BFF] bg-white"
              data-testid={`contrib-upload-moment-${item.key}`} />
          </div>
        )}

        {/* Progress bar */}
        {item.status === 'UPLOADING' && (
          <div className="relative mt-2" data-testid={`contrib-upload-progress-${item.key}`}>
            <div className="h-1.5 bg-[#E3E8EF] rounded-full overflow-hidden">
              <div className="h-full bg-[#146BFF] rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] text-[#5B6472] mt-0.5 block text-right">{pct}%</span>
          </div>
        )}

        {/* Error message */}
        {item.status === 'FAILED' && item.errorMessage && (
          <p className="text-[11px] text-[#C93434] mt-1" data-testid={`contrib-upload-error-${item.key}`}>{item.errorMessage}</p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-1 flex-shrink-0">
        {item.status === 'FAILED' && (
          <button onClick={onRetry} disabled={busy}
            className="w-8 h-8 rounded-md border border-[#E3E8EF] flex items-center justify-center hover:bg-[#F3F5F8] disabled:opacity-50"
            data-testid={`contrib-upload-retry-${item.key}`}>
            <RefreshCw className="w-3.5 h-3.5 text-[#146BFF]" />
          </button>
        )}
        {item.status === 'DELIVERED' && (
          <CheckCircle className="w-5 h-5 text-[#1F8A55]" />
        )}
        {item.status !== 'DELIVERED' && item.status !== 'UPLOADING' && (
          <button onClick={onRemove} disabled={busy}
            className="w-8 h-8 rounded-md border border-[#E3E8EF] flex items-center justify-center hover:bg-[#FEECEC] disabled:opacity-50"
            data-testid={`contrib-upload-remove-${item.key}`}>
            <X className="w-3.5 h-3.5 text-[#C93434]" />
          </button>
        )}
      </div>
    </div>
  );
}
